import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { FurnitureDefinition } from "../shared/types";
import { resolvePaletteColors } from "../shared/furniture-palette";
import { AlphaConstructionBanner } from "./components/AlphaConstructionBanner";
import { AppShell, type AppTab } from "./components/AppShell";
import { MarketplaceBrowser } from "./components/MarketplaceBrowser";
import { useFriends } from "./hooks/useFriends";
import { type SelectedAsset } from "./hooks/useMarketplace";
import { useEconomy } from "./hooks/useEconomy";
import { useRoomConnection } from "./hooks/useRoomConnection";
import { useRooms } from "./hooks/useRooms";
import { useSession } from "./hooks/useSession";
import {
  type FurnitureInventoryEntry,
  fetchFriendsPresence,
  fetchUnreadPerFriend,
  knockRoom,
  removeAdminFurniture,
  reportPlayer,
  requestRoomJoin,
  type FriendPresence,
} from "./lib/api";
import { ConnectScreen } from "./screens/ConnectScreen";
import { LobbyScreen } from "./screens/LobbyScreen";
import { AdminScreen } from "./screens/AdminScreen";
import { OnboardingScreen } from "./screens/OnboardingScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { RoomScreen } from "./screens/RoomScreen";
import { SocialScreen } from "./screens/SocialScreen";
import { FurnitureCatalogScreen } from "./screens/FurnitureCatalogScreen";
import { CreatorScreen } from "./screens/CreatorScreen";

type Screen =
  | "connect"
  | "onboarding"
  | "lobby"
  | "furniture"
  | "creator"
  | "profile"
  | "social"
  | "admin"
  | "room";

// Map AppTab ids to Screen ids
const TAB_TO_SCREEN: Record<AppTab, Screen> = {
  world: "lobby",
  furniture: "furniture",
  creator: "creator",
  profile: "profile",
  social: "social",
  admin: "admin",
};
const SCREEN_TO_TAB: Partial<Record<Screen, AppTab>> = {
  lobby: "world",
  furniture: "furniture",
  creator: "creator",
  profile: "profile",
  social: "social",
  admin: "admin",
};

export default function App() {
  const {
    session,
    loading: sessionLoading,
    connectWithKukai,
    connectWithWallet,
    disconnect,
    setAvatarColor,
    setDisplayName,
    needsOnboarding,
    completeOnboarding,
  } = useSession();

  const [screen, setScreen] = useState<Screen>(
    session ? (needsOnboarding ? "onboarding" : "lobby") : "connect"
  );
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const handleKickedFromRoom = useCallback((message: string) => {
    notifications.show({
      title: "Removed from room",
      message,
      color: "orange",
    });
    setActiveRoomId(null);
    setScreen("lobby");
  }, []);

  const {
    rooms,
    loading: roomsLoading,
    loadRooms,
    createRoom,
    deleteRoom,
    renameRoom,
    toggleRoomPrivacy,
    inviteToRoom,
    searchQuery,
    setSearchQuery,
  } = useRooms(screen !== "connect" ? session : null);
  const {
    friends,
    accepted,
    pending,
    sendRequest,
    acceptRequest,
    removeRequest,
    loadFriends,
  } = useFriends(screen !== "connect" ? session : null);
  const [dmNotificationSeq, setDmNotificationSeq] = useState(0);
  const [lastDmNotification, setLastDmNotification] = useState<{
    fromAddress: string;
    fromName: string;
    text?: string;
    seq: number;
  } | null>(null);
  const [idleWarningSeconds, setIdleWarningSeconds] = useState<number | null>(
    null
  );
  const handleIdleWarning = useCallback((secondsLeft: number) => {
    setIdleWarningSeconds(secondsLeft);
  }, []);
  const handleDMNotification = useCallback(
    (_fromAddress: string, _fromName: string, _text?: string) => {
      setDmNotificationSeq((prev) => {
        const next = prev + 1;
        setLastDmNotification({
          fromAddress: _fromAddress,
          fromName: _fromName,
          text: _text,
          seq: next,
        });
        return next;
      });
    },
    []
  );
  const handleFriendRequestNotification = useCallback(
    (_fromAddress: string, _fromName: string) => {
      notifications.show({
        title: "Friend request",
        message: `${_fromName} sent you a friend request.`,
        color: "orange",
      });
      void loadFriends();
    },
    [loadFriends]
  );
  const {
    roomState,
    stateSampleTimeMs,
    selfId,
    waitlistPosition,
    chatLog,
    connected,
    sendSocket,
    motionFeedRef,
  } = useRoomConnection(
    screen === "room" ? session : null,
    activeRoomId,
    handleKickedFromRoom,
    handleDMNotification,
    handleFriendRequestNotification,
    handleIdleWarning
  );
  const economy = useEconomy(screen !== "connect" ? session : null);

  // Friends presence polling
  const [friendsOnline, setFriendsOnline] = useState<FriendPresence[]>([]);
  useEffect(() => {
    if (!session || screen === "connect") {
      setFriendsOnline([]);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const online = await fetchFriendsPresence(session);
        if (!cancelled) setFriendsOnline(online);
      } catch {
        // ignore
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [session, screen]);

  // Per-friend unread DM counts
  const [unreadPerFriend, setUnreadPerFriend] = useState<
    Record<string, number>
  >({});
  const refreshUnreadPerFriend = useCallback(async () => {
    if (!session) return;
    try {
      const counts = await fetchUnreadPerFriend(session);
      setUnreadPerFriend(counts);
    } catch {
      // ignore
    }
  }, [session]);

  useEffect(() => {
    if (!session || screen === "connect") return;
    void refreshUnreadPerFriend();
    const timer = window.setInterval(
      () => void refreshUnreadPerFriend(),
      15_000
    );
    return () => window.clearInterval(timer);
  }, [session, screen, refreshUnreadPerFriend]);

  // Instant refresh when a DM notification arrives via WebSocket
  useEffect(() => {
    if (dmNotificationSeq) {
      void refreshUnreadPerFriend();
    }
  }, [dmNotificationSeq, refreshUnreadPerFriend]);

  const totalUnread = Object.values(unreadPerFriend).reduce(
    (sum, n) => sum + n,
    0
  );

  const [marketOpened, { open: openMarket, close: closeMarket }] =
    useDisclosure(false);
  const [externalAsset, setExternalAsset] = useState<SelectedAsset | null>(
    null
  );
  const [creatorEditingItem, setCreatorEditingItem] =
    useState<FurnitureDefinition | null>(null);
  const createSelectedFurnitureAsset = useCallback(
    (
      item: FurnitureDefinition,
      colorVariantId?: string,
      paletteColors?: Record<string, string>,
      resolveFallbackVariant = true
    ): SelectedAsset => {
      const variants = item.colorVariants ?? [];
      let resolvedColorVariantId: string | undefined;
      if (variants.length > 0) {
        const requested = colorVariantId?.trim() ?? "";
        if (requested) {
          resolvedColorVariantId =
            variants.find((variant) => variant.id === requested)?.id ??
            (resolveFallbackVariant
              ? variants.find(
                  (variant) => variant.id === item.defaultColorVariantId
                )?.id ??
                variants[0]?.id
              : requested);
        } else if (resolveFallbackVariant) {
          resolvedColorVariantId =
            variants.find((variant) => variant.id === item.defaultColorVariantId)
              ?.id ?? variants[0]?.id;
        } else {
          resolvedColorVariantId = undefined;
        }
      } else {
        resolvedColorVariantId = undefined;
      }
      const resolvedPaletteColors =
        paletteColors ??
        resolvePaletteColors(
          item.colorVariants,
          resolvedColorVariantId,
          item.defaultColorVariantId
        );
      return {
        type: "glb",
        url: item.modelUrl,
        name: item.name,
        thumbnail: item.thumbnailUrl,
        furnitureId: item.id,
        tintColor: item.tintColor,
        blockWidth: item.blockWidth,
        blockDepth: item.blockDepth,
        sittable: item.sittable,
        interactionType: item.interactionType,
        walkableTop: item.walkableTop,
        stackableTop: item.stackableTop,
        stackHeight: item.stackHeight,
        actionOpenAnimation: item.actionOpenAnimation,
        actionCloseAnimation: item.actionCloseAnimation,
        seatOffsetX: item.seatOffsetX,
        seatOffsetY: item.seatOffsetY,
        seatOffsetZ: item.seatOffsetZ,
        colorSlots: item.colorSlots,
        colorVariants: item.colorVariants,
        defaultColorVariantId: item.defaultColorVariantId,
        colorVariantId: resolvedColorVariantId,
        paletteColors: resolvedPaletteColors,
      };
    },
    []
  );

  useEffect(() => {
    if (!session) {
      setCreatorEditingItem(null);
      setScreen("connect");
      return;
    }
    if (!session.isAdmin && screen === "admin") {
      setScreen("lobby");
      return;
    }
    if (needsOnboarding && screen !== "onboarding") {
      setScreen("onboarding");
      return;
    }
    if (!needsOnboarding && screen === "connect") {
      setScreen("lobby");
    }
  }, [needsOnboarding, screen, session]);

  const handleConnect = async () => {
    const result = await connectWithWallet();
    setScreen(result.isFirstLogin ? "onboarding" : "lobby");
    return result.session;
  };

  const handleKukaiConnect = async () => {
    const result = await connectWithKukai();
    setScreen(result.isFirstLogin ? "onboarding" : "lobby");
    return result.session;
  };

  const handleJoinRoom = async (roomId: string) => {
    if (!session) return;
    let passwordAttempt: string | undefined;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const decision = await requestRoomJoin(
          session,
          roomId,
          passwordAttempt
        );
        if (decision.allowed) {
          setActiveRoomId(roomId);
          setScreen("room");
          return;
        }
        if (decision.reason === "password_required") {
          const password = window.prompt("Enter room password");
          if (!password) return;
          passwordAttempt = password;
          continue;
        }
        if (decision.canKnock) {
          const shouldKnock = window.confirm(
            "You cannot enter directly. Send a knock request to the room owner?"
          );
          if (!shouldKnock) {
            return;
          }
          await knockRoom(session, roomId);
          notifications.show({
            title: "Knock sent",
            message: "The room owner can now admit you.",
            color: "teal",
          });
          return;
        }
        notifications.show({
          title: "Room access denied",
          message:
            decision.reason === "token_required"
              ? "This room requires the configured token."
              : "You need owner approval to enter this room.",
          color: "orange",
        });
        return;
      } catch (error) {
        notifications.show({
          title: "Could not join room",
          message: error instanceof Error ? error.message : "Please retry.",
          color: "red",
        });
        return;
      }
    }
  };

  const handleKnockRoom = async (roomId: string) => {
    if (!session) return;
    try {
      await knockRoom(session, roomId);
      notifications.show({
        title: "Knock sent",
        message: "The room owner can now admit you.",
        color: "teal",
      });
    } catch (error) {
      notifications.show({
        title: "Knock failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    }
  };

  const activeRoom = activeRoomId
    ? rooms.find((room) => room.id === activeRoomId) ?? null
    : null;

  const handleBackToLobby = () => {
    setActiveRoomId(null);
    setScreen("lobby");
    setIdleWarningSeconds(null);
  };

  const handleDisconnect = () => {
    setActiveRoomId(null);
    disconnect();
    setScreen("connect");
  };

  const handleTabChange = (tab: AppTab) => {
    if (!session) {
      return;
    }
    if (tab === "admin" && !session.isAdmin) {
      setScreen("lobby");
      return;
    }
    if (tab === "creator") {
      setCreatorEditingItem(null);
      setScreen("creator");
      return;
    }
    setCreatorEditingItem(null);
    setScreen(TAB_TO_SCREEN[tab]);
  };

  const withAlphaBanner = (content: ReactNode) => (
    <>
      <AlphaConstructionBanner />
      {content}
    </>
  );

  // ---- Fullscreen screens (no AppShell) ----

  if (screen === "connect" || !session) {
    return withAlphaBanner(
      <ConnectScreen
        loading={sessionLoading}
        onConnectKukai={handleKukaiConnect}
        onConnectWallet={handleConnect}
      />
    );
  }

  if (screen === "onboarding") {
    return withAlphaBanner(
      <OnboardingScreen
        session={session}
        onComplete={async (name, color) => {
          await setDisplayName(name);
          setAvatarColor(color);
          await loadRooms();
          completeOnboarding();
          setScreen("lobby");
        }}
      />
    );
  }

  if (screen === "room") {
    return withAlphaBanner(
      <>
        <RoomScreen
          session={session}
          roomId={activeRoomId!}
          roomName={activeRoom?.name}
          roomOwnerAddress={activeRoom?.ownerAddress}
          roomIsPrivate={activeRoom?.isPrivate}
          roomJoinMode={activeRoom?.joinMode}
          roomTokenGateContract={activeRoom?.tokenGateContract}
          roomAllowKnock={activeRoom?.allowKnock}
          roomState={roomState}
          stateSampleTimeMs={stateSampleTimeMs}
          selfId={selfId}
          waitlistPosition={waitlistPosition}
          chatLog={chatLog}
          connected={connected}
          sendSocket={sendSocket}
          motionFeedRef={motionFeedRef}
          onBack={handleBackToLobby}
          onOpenMarketplace={openMarket}
          onRefreshFurnitureInventory={economy.refreshInventory}
          furnitureInventory={economy.inventory}
          onSelectFurniture={(entry: FurnitureInventoryEntry) => {
            setExternalAsset(
              createSelectedFurnitureAsset(
                entry.item,
                entry.colorVariantId,
                entry.paletteColors,
                false
              )
            );
          }}
          friendLinks={friends}
          pendingFriends={pending}
          onAcceptFriend={acceptRequest}
          onRemoveFriend={removeRequest}
          onAddFriend={sendRequest}
          friendsOnline={friendsOnline}
          dmNotificationSeq={dmNotificationSeq}
          lastDmNotification={lastDmNotification}
          unreadPerFriend={unreadPerFriend}
          onRefreshUnread={() => void refreshUnreadPerFriend()}
          idleWarningSeconds={idleWarningSeconds}
          onDismissIdleWarning={() => setIdleWarningSeconds(null)}
          onVisitFriendRoom={(roomId) => {
            handleBackToLobby();
            void handleJoinRoom(roomId);
          }}
          onReportUser={(address, targetRoomId) =>
            reportPlayer(session, address, targetRoomId)
          }
          onRoomSettingsUpdated={() => {
            void loadRooms();
          }}
          externalSelectedAsset={externalAsset}
          onClearExternalAsset={() => setExternalAsset(null)}
        />
        {marketOpened && (
          <MarketplaceBrowser
            session={session}
            coins={economy.coins}
            coinPackages={economy.packages}
            furnitureCatalog={economy.catalog}
            furnitureInventory={economy.inventory}
            economyLoading={economy.loading}
            onRefreshEconomy={economy.refreshInventory}
            onBuyCoins={async (packageId) => {
              await economy.purchaseCoins(packageId);
            }}
            onBuyFurniture={async (furnitureId, colorVariantId) => {
              await economy.purchaseFurniture(furnitureId, 1, colorVariantId);
            }}
            onSelectFurniture={(item, colorVariantId) => {
              setExternalAsset(
                createSelectedFurnitureAsset(item, colorVariantId)
              );
              closeMarket();
            }}
            onClose={closeMarket}
          />
        )}
      </>
    );
  }

  // ---- Screens within AppShell ----

  const activeTab = SCREEN_TO_TAB[screen] ?? "world";

  const renderContent = () => {
    switch (screen) {
      case "profile":
        return (
          <ProfileScreen
            session={session}
            onSave={async (name, color) => {
              await setDisplayName(name);
              setAvatarColor(color);
              await loadRooms();
            }}
          />
        );
      case "social":
        return (
          <SocialScreen
            session={session}
            acceptedFriends={accepted}
            pendingFriends={pending}
            friendsOnline={friendsOnline}
            unreadPerFriend={unreadPerFriend}
            lastDmNotification={lastDmNotification}
            onSendFriendRequest={sendRequest}
            onAcceptFriend={acceptRequest}
            onRemoveFriend={removeRequest}
            onVisitRoom={(roomId) => void handleJoinRoom(roomId)}
            onUnreadChange={() => void refreshUnreadPerFriend()}
          />
        );
      case "furniture":
        return (
          <FurnitureCatalogScreen
            catalog={economy.catalog}
            inventory={economy.inventory}
            loading={economy.loading}
            onRefresh={economy.load}
            onBuyFurniture={async (furnitureId, colorVariantId) => {
              await economy.purchaseFurniture(furnitureId, 1, colorVariantId);
            }}
            onRemoveFurniture={
              session.isAdmin
                ? async (furnitureId) => {
                    await removeAdminFurniture(session, furnitureId);
                    await economy.load();
                  }
                : undefined
            }
            isAdmin={session.isAdmin}
            currentAddress={session.address}
            onEditFurniture={(item) => {
              const isOwnDraft =
                item.itemState === "draft" &&
                (item.sandboxOwnerAddress?.trim().toLowerCase() ===
                  session.address.trim().toLowerCase() ||
                  item.createdBy?.trim().toLowerCase() ===
                    session.address.trim().toLowerCase());
              if (!session.isAdmin && !isOwnDraft) {
                return;
              }
              setCreatorEditingItem(item);
              setScreen("creator");
            }}
          />
        );
      case "creator":
        return (
          <CreatorScreen
            session={session}
            onCreated={economy.load}
            editingFurniture={creatorEditingItem}
            onDoneEditing={() => {
              setCreatorEditingItem(null);
              setScreen("furniture");
            }}
          />
        );
      case "admin":
        return <AdminScreen session={session} />;
      case "lobby":
      default:
        return (
          <LobbyScreen
            session={session}
            rooms={rooms}
            roomsLoading={roomsLoading}
            searchQuery={searchQuery}
            onSearchRooms={setSearchQuery}
            onJoinRoom={handleJoinRoom}
            onCreateRoom={createRoom}
            onDeleteRoom={deleteRoom}
            onRenameRoom={renameRoom}
            onToggleRoomPrivacy={toggleRoomPrivacy}
            onKnockRoom={handleKnockRoom}
            friendsOnline={friendsOnline}
          />
        );
    }
  };

  return withAlphaBanner(
    <AppShell
      session={session}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onDisconnect={handleDisconnect}
      unreadSocialCount={totalUnread}
      showAdminTab={session.isAdmin}
      showCreatorTab
    >
      {renderContent()}
    </AppShell>
  );
}
